import {
  AlertCircle,
  Boxes,
  ClipboardList,
  Edit3,
  ListTree,
  PackageCheck,
  Plus,
  Search,
  Truck,
  Warehouse,
  X
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import { MOCK_BOOKS } from '../../../../mocks/Books.mocks';
import { MOCK_WAREHOUSES } from '../../../../mocks/Warehouses.mocks';
import { Book, InventoryAudit, MerchandiseReceipt, Society, Warehouse as WarehouseType } from '../../../../types';
import { useToast } from '../../../../hooks/useToast';
import Modal from '../../../../components/shared/Modal/Modal';
import { listWarehouses, createWarehouse, updateWarehouse } from '../../../../services/WarehousesService';
import { listWarehouseStock, WarehouseStockRow } from '../../../../services/StockMovementsService';
import { listBooks } from '../../../../services/BooksService';
import { listCompanies } from '../../../../services/CompaniesService';
import { DataTable, ColumnDef } from '../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction, deleteAction } from '../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailCard,
  DetailHeaderButton,
  DetailIdentity
} from '../../../../components/shared/DetailPanel/DetailBlocks';
import { SidebarForm } from '../../../../components/shared/SidebarForm/SidebarForm';
import { useClickOutside, isDetailKeepZone } from '../../../../hooks/useClickOutside';
import { GoodsReceiptScreen } from './GoodsReceiptScreen';
import { NeedsListScreen } from './NeedsListScreen';
import '../shared-modules.css';
import './WarehousesView.css';

// La pestaña activa va en ?pantalla= para que sobreviva al remontaje de la
// vista cuando se cambia de sociedad en el header.
const WAREHOUSE_TABS = [
  { id: 'almacenes', label: 'Almacenes', icon: Warehouse },
  { id: 'recepcion', label: 'Recepción_De_Mercancía', icon: PackageCheck },
  { id: 'necesidades', label: 'Lista_De_Necesidades', icon: ListTree }
];

export const WarehousesView = () => {
  const { notify } = useToast();
  const location = useLocation();
  const history = useHistory();
  const tab = new URLSearchParams(location.search).get('pantalla') || 'almacenes';
  const [warehouses, setWarehouses] = useState<WarehouseType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [societies, setSocieties] = useState<Society[]>([]);
  const [books, setBooks] = useState<Book[]>(MOCK_BOOKS);

  useEffect(() => {
    let cancelled = false;
    // listWarehouses cae a MOCK_WAREHOUSES si el endpoint todavía no responde,
    // así que la vista nunca se queda sin datos con los que trabajar.
    listWarehouses()
      .then(list => { if (!cancelled) setWarehouses(list); })
      .catch(err => {
        console.error('Error fetching warehouses:', err);
        if (!cancelled) setWarehouses(MOCK_WAREHOUSES);
      })
      .then(() => { if (!cancelled) setIsLoading(false); });

    listBooks()
      .then(list => { if (!cancelled) setBooks(list); })
      .catch(err => console.error('Error fetching books:', err));

    // Sociedades reales: warehouses_create guarda el company_id tal cual
    listCompanies()
      .then(list => { if (!cancelled) setSocieties(list); })
      .catch(err => console.error('Error fetching companies:', err));

    return () => { cancelled = true; };
  }, []);

  const [selectedWarehouse, setSelectedWarehouse] = useState<WarehouseType | null>(null);
  const [deleteModalWarehouse, setDeleteModalWarehouse] = useState<{ id: string; name: string } | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activePhotoTab, setActivePhotoTab] = useState<'layout' | 'hierarchy'>('layout');

  // Inventory Management State
  const [isInventoryOpen, setIsInventoryOpen] = useState(false);
  // Existencias reales del almacén abierto en la auditoría (suma de sus movimientos de stock).
  const [warehouseStock, setWarehouseStock] = useState<WarehouseStockRow[]>([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockError, setStockError] = useState('');
  const [selectedAudit, setSelectedAudit] = useState<InventoryAudit | null>(null);
  const [newAuditResponsible, setNewAuditResponsible] = useState('Raúl Nogales');
  const [newAuditDate, setNewAuditDate] = useState(new Date().toISOString().split('T')[0]);
  const [newAuditNotes, setNewAuditNotes] = useState('');
  const [newCounts, setNewCounts] = useState<Record<string, number>>({});
  const [inventoryLogs] = useState<InventoryAudit[]>([
    {
      id: 'INV-001', warehouseId: 'WH-01', warehouseName: 'Madrid Central',
      date: '2026-07-13', responsibleName: 'Carlos Ruiz', expectedItems: 980,
      countedItems: 972, difference: -8, status: 'discrepancy',
      notes: 'Ajuste periódico.',
      details: [
        { bookId: 'BK-001', bookTitle: 'Cien Años de Soledad', expectedStock: 250, countedStock: 242, difference: -8 },
        { bookId: 'BK-002', bookTitle: 'Don Quijote', expectedStock: 330, countedStock: 330, difference: 0 }
      ]
    }
  ]);

  // Merchandise Receipt
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState<MerchandiseReceipt | null>(null);
  const [newReceiptSource, setNewReceiptSource] = useState('Gráficas Madrid S.A.');
  const [newReceiptNoteNum, setNewReceiptNoteNum] = useState('ALB-2026-089');
  const [newReceiptReceiver, setNewReceiptReceiver] = useState('Raúl Nogales');
  const [newReceiptDate, setNewReceiptDate] = useState(new Date().toISOString().split('T')[0]);
  const [newReceiptNotes, setNewReceiptNotes] = useState('');
  const [newReceiptQuantities, setNewReceiptQuantities] = useState<Record<string, number>>({});
  const [receiptLogs] = useState<MerchandiseReceipt[]>([
    {
      id: 'REC-001', warehouseId: 'WH-01', warehouseName: 'Madrid Central',
      date: '2026-07-20', receiverName: 'Carlos Ruiz', source: 'Imprenta Sol', deliveryNoteNumber: 'ALB-99812',
      totalItemsReceived: 350, notes: 'Recepción sin incidencias',
      details: [{ bookId: 'BK-001', bookTitle: 'Cien Años de Soledad', quantityReceived: 150, previousStock: 250, newStock: 400 }]
    }
  ]);

  const handleOpenInventory = (wh: WarehouseType) => {
    setSelectedWarehouse(wh);
    setWarehouseStock([]);
    setNewCounts({});
    setStockError('');
    setStockLoading(true);
    listWarehouseStock(wh.id)
      .then(rows => {
        setWarehouseStock(rows);
        const initialCounts: Record<string, number> = {};
        rows.forEach(r => { initialCounts[r.bookId] = r.stock; });
        setNewCounts(initialCounts);
      })
      .catch(err => setStockError((err as Error)?.message || 'No se pudo cargar el stock del almacén.'))
      .finally(() => setStockLoading(false));
    setNewAuditResponsible('Raúl Nogales');
    setNewAuditDate(new Date().toISOString().split('T')[0]);
    setNewAuditNotes('');
    setSelectedAudit(null);
    setIsInventoryOpen(true);
  };

  const handleOpenReceipt = (wh: WarehouseType) => {
    setSelectedWarehouse(wh);
    const initialQuantities: Record<string, number> = {};
    books.forEach(b => { initialQuantities[b.id] = 0; });
    setNewReceiptQuantities(initialQuantities);
    setNewReceiptSource('Gráficas Madrid');
    setNewReceiptNoteNum(`ALB-2026-${Math.floor(100 + Math.random() * 900)}`);
    setNewReceiptReceiver('Raúl Nogales');
    setNewReceiptDate(new Date().toISOString().split('T')[0]);
    setNewReceiptNotes('');
    setSelectedReceipt(null);
    setIsReceiptOpen(true);
  };

  const getIncomingTotal = () => Object.values(newReceiptQuantities).reduce((acc, val) => acc + (val || 0), 0);

  const [formData, setFormData] = useState<Omit<WarehouseType, 'id'>>({
    name: '', societyId: societies[0]?.id || '', type: 'physical', managementModel: 'location',
    capacity: 20000, used: 5000, activeShipments: 0, parentPhysicalWarehouseId: '',
    address: '', description: ''
  });
  const [formError, setFormError] = useState<string | null>(null);

  const handleCreateNewClick = () => {
    setSelectedWarehouse(null);
    setFormData({
      name: '', societyId: societies[0]?.id || '', type: 'physical', managementModel: 'location',
      capacity: 30000, used: 0, activeShipments: 0, parentPhysicalWarehouseId: '', address: '', description: ''
    });
    setFormError(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (wh: WarehouseType) => {
    setSelectedWarehouse(wh);
    setFormData({
      name: wh.name, societyId: wh.societyId, type: wh.type, managementModel: wh.managementModel,
      capacity: wh.capacity, used: wh.used, activeShipments: wh.activeShipments,
      parentPhysicalWarehouseId: wh.parentPhysicalWarehouseId || '', address: wh.address || '', description: wh.description || ''
    });
    setFormError(null);
    setIsFormOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return setFormError('El nombre es obligatorio.');
    if (!formData.societyId) return setFormError('Debe ligar a una sociedad.');
    if (formData.capacity <= 0) return setFormError('Capacidad debe ser > 0.');
    if (formData.used > formData.capacity) return setFormError('Usado no puede superar capacidad.');

    const payload: Omit<WarehouseType, 'id'> = {
      ...formData,
      parentPhysicalWarehouseId: formData.type === 'virtual' && formData.parentPhysicalWarehouseId ? formData.parentPhysicalWarehouseId : undefined
    };

    try {
      if (selectedWarehouse) {
        const updated = await updateWarehouse(selectedWarehouse.id, payload);
        // El backend no guarda capacidad, ocupación ni dirección, así que se
        // conservan los valores del formulario sobre la respuesta.
        const merged: WarehouseType = { ...payload, ...updated, id: updated.id };
        setWarehouses(prev => prev.map(w => w.id === selectedWarehouse.id ? merged : w));
        setSelectedWarehouse(merged);
        notify('Almacén actualizado', 'Los datos del almacén se han guardado correctamente.', 'info');
      } else {
        const created = await createWarehouse(payload);
        const merged: WarehouseType = { ...payload, ...created, id: created.id };
        setWarehouses(prev => [...prev, merged]);
        setSelectedWarehouse(merged);
        notify('Almacén creado', 'El nuevo almacén se ha registrado correctamente.', 'info');
      }
      setIsFormOpen(false);
    } catch (err) {
      console.error('Error saving warehouse:', err);
      setFormError(err instanceof Error ? err.message : 'Error al guardar el almacén.');
    }
  };

  const handleDelete = (whId: string, whName: string) => {
    const linked = warehouses.filter(w => w.parentPhysicalWarehouseId === whId);
    if (linked.length > 0) {
      notify('Operación no permitida', 'No se puede eliminar porque contiene almacenes virtuales.', 'warning');
      return;
    }
    setDeleteModalWarehouse({ id: whId, name: whName });
  };

  const confirmDeleteWarehouse = () => {
    if (!deleteModalWarehouse) return;
    const { id } = deleteModalWarehouse;
    setWarehouses(prev => prev.filter(w => w.id !== id));
    if (selectedWarehouse?.id === id) setSelectedWarehouse(null);
    setDeleteModalWarehouse(null);
    notify('Éxito', 'Almacén eliminado exitosamente', 'info');
  };

  const filteredWarehouses = warehouses.filter(w => w.name.toLowerCase().includes(searchQuery.toLowerCase()) || w.id.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleSelectWarehouse = (wh: WarehouseType) => {
    setIsFormOpen(false);
    setSelectedWarehouse(wh);
  };

  const closePanel = () => {
    setIsFormOpen(false);
    setSelectedWarehouse(null);
  };

  // replace y no push, como en Finanzas: el botón atrás no recorre las pestañas
  const switchTab = (next: string) => {
    if (next === tab) return;
    closePanel();
    const params = new URLSearchParams(location.search);
    params.set('pantalla', next);
    history.replace({ search: `?${params.toString()}` });
  };

  // Clic fuera cierra la ficha. El formulario no: esta vista no avisa de
  // cambios sin guardar y se perdería lo escrito; se cierra con la X.
  // Los modales de Inventario y Recepción llevan data-keep-detail.
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedWarehouse) && !isFormOpen);

  const columns: ColumnDef<WarehouseType>[] = [
    {
      id: 'id',
      header: 'ALM_ID',
      render: (wh) => <span className="warehouses-view__list-id">{wh.id}</span>
    },
    {
      id: 'name',
      header: 'ALMACÉN',
      render: (wh) => <span className="warehouses-view__list-name">{wh.name}</span>
    },
    {
      id: 'type',
      header: 'TIPO',
      align: 'center',
      render: (wh) => (
        <span className={`module-badge ${wh.type === 'physical' ? 'active' : 'inactive'}`} style={{ color: wh.type === 'physical' ? 'var(--color-brand-green)' : '#38bdf8', borderColor: 'currentColor', backgroundColor: 'transparent' }}>
          {wh.type === 'physical' ? 'FÍSICO' : 'VIRTUAL'}
        </span>
      )
    },
    {
      id: 'management',
      header: 'GESTIÓN',
      render: (wh) => (wh.managementModel === 'location' ? 'POR UBICACIÓN' : 'CAÓTICO')
    }
  ];

  const renderLocationGrid = (wh: WarehouseType) => {
    const aisles = ['Pasillo A', 'Pasillo B'];
    const levels = [3, 2, 1];
    const sections = [1, 2, 3, 4];
    return (
      <div className="warehouses-view__layout-container">
        {aisles.map(aisle => (
          <div key={aisle} className="warehouses-view__layout-aisle">
            <h5 style={{ fontSize: '0.625rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: '#fff', textTransform: 'uppercase', marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
              <span>{aisle}</span>
              <span style={{ color: 'var(--color-brand-green)' }}>Cap: 92%</span>
            </h5>
            <div className="warehouses-view__layout-grid">
              {levels.map(level => sections.map(section => {
                const code = (wh.id.charCodeAt(3) || 7) + aisle.charCodeAt(8) + section * 17 + level * 31;
                const fill = code % 100 < 20 ? 'empty' : code % 100 < 60 ? 'medium' : 'full';
                return (
                  <div key={`${level}-${section}`} className={`warehouses-view__layout-cell ${fill}`}>
                    <span style={{ fontSize: '0.375rem', fontFamily: 'var(--font-mono)' }}>{aisle.split(' ')[1]}{section}{level}</span>
                    <div style={{ textAlign: 'right', fontSize: '0.4375rem', fontWeight: 700 }}>{fill === 'empty' ? '0%' : fill === 'medium' ? '45%' : '100%'}</div>
                  </div>
                );
              }))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderChaoticLayout = (wh: WarehouseType) => {
    const zones = [
      { name: 'Recepción', cap: 75, color: 'rgba(202, 138, 4, 0.2)', border: 'rgba(202, 138, 4, 0.4)', text: '#facc15' },
      { name: 'Granel Alta Rotación', cap: 91, color: 'rgba(100, 255, 218, 0.1)', border: 'rgba(100, 255, 218, 0.4)', text: 'var(--color-brand-green)' },
      { name: 'Granel Media Rotación', cap: 43, color: 'rgba(8, 47, 73, 0.4)', border: 'rgba(14, 165, 233, 0.4)', text: '#38bdf8' }
    ];
    return (
      <div className="warehouses-view__layout-container">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          {zones.map(z => (
            <div key={z.name} style={{ padding: '1rem', backgroundColor: z.color, border: `1px solid ${z.border}`, color: z.text }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <h5 style={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase' }}>{z.name}</h5>
                <span style={{ fontSize: '0.5rem', fontWeight: 700, padding: '0.125rem 0.375rem', border: '1px solid currentColor' }}>{z.cap}%</span>
              </div>
              <div className="warehouses-view__chaotic-grid">
                {Array.from({ length: 16 }).map((_, i) => (
                  <div key={i} className={`warehouses-view__chaotic-cell ${(i * 7) % 100 < z.cap ? 'filled' : 'empty'}`}></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <MasterDetailLayout isOpen={Boolean(selectedWarehouse || isFormOpen)} drawer>
        <div className="warehouses-view">
          <div className="module-header-row">
            <ModuleHeader title="Control_De_Almacenes" subtitle="Alta de almacenes físicos o virtuales, recepción de mercancía contra pedido y lista de necesidades por libro." />
            {tab === 'almacenes' && (
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={handleCreateNewClick} className="module-create-btn" data-keep-detail><Plus size={14} /> Alta_Almacén</button>
              </div>
            )}
          </div>

          <div className="module-tabs">
            {WAREHOUSE_TABS.map(({ id, label, icon: Icon }) => (
              <button key={id} type="button" onClick={() => switchTab(id)} className={`module-tab ${tab === id ? 'active' : ''}`}>
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>

          {tab === 'almacenes' ? (
            <>
              <div className="module-toolbar">
                <div className="module-search-wrapper">
                  <Search className="module-search-icon" size={14} />
                  <input type="text" placeholder="BUSCAR ALMACÉN POR NOMBRE..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="module-search-input" />
                </div>
              </div>

              <DataTable
                data={filteredWarehouses}
                columns={columns}
                title={<><Warehouse size={14} className="module-panel-title-icon" /> Almacenes</>}
                subtitle={isLoading ? undefined : `TOTAL: ${filteredWarehouses.length}`}
                isLoading={isLoading}
                getRowId={(wh) => wh.id}
                onRowClick={handleSelectWarehouse}
                selectedRowId={selectedWarehouse?.id}
                emptyText="No se encontraron almacenes."
                actionsHeader="ACCIONES"
                actionsColumn={(wh) => (
                  <RowActionsMenu
                    actions={[
                      viewAction(() => handleSelectWarehouse(wh)),
                      editAction(() => handleEditClick(wh)),
                      deleteAction(() => handleDelete(wh.id, wh.name))
                    ]}
                  />
                )}
              />
            </>
          ) : tab === 'recepcion' ? (
            <GoodsReceiptScreen />
          ) : (
            <NeedsListScreen />
          )}
        </div>

        {isFormOpen ? (
          <SidebarForm
            drawer
            title={selectedWarehouse ? 'Configurar_Almacén' : 'Alta_Almacén'}
            onClose={closePanel}
            submitLabel="Guardar"
            onSubmit={handleSave}
            showDelete={Boolean(selectedWarehouse)}
            onDelete={() => selectedWarehouse && handleDelete(selectedWarehouse.id, selectedWarehouse.name)}
          >
            {formError && <div className="module-form-error"><AlertCircle size={14} style={{ flexShrink: 0 }} /><span>{formError}</span></div>}
            <div className="sidebar-form-grid-2">
              <div className="sidebar-form-group"><label className="sidebar-form-label">Nombre *</label><input type="text" value={formData.name} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, name: val })); }} className="sidebar-form-input" /></div>
              <div className="sidebar-form-group"><label className="sidebar-form-label">Sociedad *</label><select value={formData.societyId} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, societyId: val })); }} className="sidebar-form-input"><option value="">Seleccione...</option>{societies.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            </div>
            <div className="sidebar-form-grid-2">
              <div className="sidebar-form-group"><label className="sidebar-form-label">Tipo *</label><select value={formData.type} onChange={(e) => { const val = e.target.value as 'physical' | 'virtual'; setFormData(prev => ({ ...prev, type: val })); }} className="sidebar-form-input"><option value="physical">FÍSICO</option><option value="virtual">VIRTUAL</option></select></div>
              <div className="sidebar-form-group"><label className="sidebar-form-label">Modelo de Gestión *</label><select value={formData.managementModel} onChange={(e) => { const val = e.target.value as WarehouseType['managementModel']; setFormData(prev => ({ ...prev, managementModel: val })); }} className="sidebar-form-input"><option value="location">POR UBICACIÓN</option><option value="chaotic">CAÓTICO</option></select></div>
            </div>
            {formData.type === 'virtual' && (
              <div className="sidebar-form-group"><label className="sidebar-form-label">Pertenece al Almacén Físico</label><select value={formData.parentPhysicalWarehouseId} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, parentPhysicalWarehouseId: val })); }} className="sidebar-form-input"><option value="">No ligar</option>{warehouses.filter(w => w.type === 'physical' && w.id !== selectedWarehouse?.id).map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
            )}
            <div className="sidebar-form-grid-2">
              <div className="sidebar-form-group"><label className="sidebar-form-label">Capacidad</label><input type="number" value={formData.capacity} onChange={(e) => { const val = Number(e.target.value); setFormData(prev => ({ ...prev, capacity: val })); }} className="sidebar-form-input" /></div>
              <div className="sidebar-form-group"><label className="sidebar-form-label">Ocupado</label><input type="number" value={formData.used} onChange={(e) => { const val = Number(e.target.value); setFormData(prev => ({ ...prev, used: val })); }} className="sidebar-form-input" /></div>
            </div>
            <div className="sidebar-form-group"><label className="sidebar-form-label">Envíos Activos</label><input type="number" value={formData.activeShipments} onChange={(e) => { const val = Number(e.target.value); setFormData(prev => ({ ...prev, activeShipments: val })); }} className="sidebar-form-input" /></div>
            <div className="sidebar-form-group"><label className="sidebar-form-label">Dirección</label><input type="text" value={formData.address} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, address: val })); }} className="sidebar-form-input" /></div>
            <div className="sidebar-form-group"><label className="sidebar-form-label">Descripción</label><textarea rows={2} value={formData.description} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, description: val })); }} className="sidebar-form-input" style={{ resize: 'none' }}></textarea></div>
          </SidebarForm>
        ) : selectedWarehouse ? (
          <DetailPanel
            drawer
            title="Ficha_De_Almacén"
            onClose={closePanel}
            headerActions={
              <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEditClick(selectedWarehouse)} />
            }
            toolbar={
              <>
                <button onClick={() => handleOpenReceipt(selectedWarehouse)} className="warehouses-view__action-btn-blue"><Truck size={12} /> Recibir</button>
                <button onClick={() => handleOpenInventory(selectedWarehouse)} className="warehouses-view__action-btn-green"><ClipboardList size={12} /> Inventario</button>
              </>
            }
            cards={
              <>
                <DetailCard icon={Boxes} title="Foto Resumen">
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => setActivePhotoTab('layout')} className={`module-badge ${activePhotoTab === 'layout' ? 'active' : 'inactive'}`} style={{ cursor: 'pointer', backgroundColor: activePhotoTab === 'layout' ? 'var(--color-brand-green)' : 'transparent', color: activePhotoTab === 'layout' ? 'var(--color-brand-blue)' : 'var(--color-brand-text)', border: `1px solid ${activePhotoTab === 'layout' ? 'var(--color-brand-green)' : 'var(--color-brand-blue-lighter)'}` }}>Distribución</button>
                    <button onClick={() => setActivePhotoTab('hierarchy')} className={`module-badge ${activePhotoTab === 'hierarchy' ? 'active' : 'inactive'}`} style={{ cursor: 'pointer', backgroundColor: activePhotoTab === 'hierarchy' ? 'var(--color-brand-green)' : 'transparent', color: activePhotoTab === 'hierarchy' ? 'var(--color-brand-blue)' : 'var(--color-brand-text)', border: `1px solid ${activePhotoTab === 'hierarchy' ? 'var(--color-brand-green)' : 'var(--color-brand-blue-lighter)'}` }}>Jerarquía</button>
                  </div>
                  {activePhotoTab === 'layout' ? (
                    selectedWarehouse.managementModel === 'location' ? renderLocationGrid(selectedWarehouse) : renderChaoticLayout(selectedWarehouse)
                  ) : (
                    <div style={{ fontSize: '0.625rem', fontFamily: 'var(--font-mono)', color: 'var(--color-brand-text)' }}>
                      <p>Visualización de almacenes {selectedWarehouse.type === 'physical' ? 'virtuales contenidos' : 'físicos asignados'}.</p>
                      {selectedWarehouse.type === 'physical' ? (
                        <ul style={{ marginTop: '1rem', paddingLeft: '1rem', color: '#fff' }}>
                          {warehouses.filter(w => w.parentPhysicalWarehouseId === selectedWarehouse.id).map(v => <li key={v.id}>{v.name} ({v.id})</li>)}
                          {warehouses.filter(w => w.parentPhysicalWarehouseId === selectedWarehouse.id).length === 0 && <li style={{ color: 'var(--color-brand-text)' }}>Sin almacenes virtuales vinculados.</li>}
                        </ul>
                      ) : (
                        <p style={{ marginTop: '1rem', color: '#fff' }}>Almacén Físico: {warehouses.find(w => w.id === selectedWarehouse.parentPhysicalWarehouseId)?.name || 'Ninguno asignado'}</p>
                      )}
                    </div>
                  )}
                </DetailCard>
              </>
            }
          >
            <DetailIdentity
              icon={Warehouse}
              reference={selectedWarehouse.id}
              name={selectedWarehouse.name}
              subtitle={
                <span className="module-badge" style={{ color: '#38bdf8', borderColor: 'currentColor', backgroundColor: 'transparent' }}>
                  {selectedWarehouse.type === 'physical' ? 'FÍSICO' : 'VIRTUAL'}
                </span>
              }
            />

            <div className="module-stats-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <div className="module-stat-card"><span className="module-stat-label">Capacidad Utilizada</span><span className="module-stat-value">{Math.round((selectedWarehouse.used / selectedWarehouse.capacity) * 100)}%</span></div>
              <div className="module-stat-card"><span className="module-stat-label">Envíos Activos</span><span className="module-stat-value" style={{ color: 'var(--color-brand-green)' }}>{selectedWarehouse.activeShipments}</span></div>
              <div className="module-stat-card"><span className="module-stat-label">Tipo Gestión</span><span className="module-stat-value" style={{ fontSize: '0.875rem' }}>{selectedWarehouse.managementModel === 'location' ? 'UBICACIÓN' : 'CAÓTICO'}</span></div>
              <div className="module-stat-card"><span className="module-stat-label">Eficiencia Espacial</span><span className="module-stat-value">{selectedWarehouse.managementModel === 'location' ? '88.5%' : '94.2%'}</span></div>
            </div>
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>

      {/* Inventory Modal */}
      {isInventoryOpen && selectedWarehouse && (
        <div className="warehouses-view__modal-overlay" data-keep-detail>
          <div className="warehouses-view__modal-container">
            <div className="warehouses-view__modal-header">
              <div>
                <h3 className="warehouses-view__modal-title"><ClipboardList size={16} style={{ color: 'var(--color-brand-green)' }} /> Auditoría de Inventario</h3>
                <p className="warehouses-view__modal-subtitle">Almacén: <strong>{selectedWarehouse.name}</strong></p>
              </div>
              <button onClick={() => setIsInventoryOpen(false)} className="warehouses-view__modal-close"><X size={16} /></button>
            </div>
            <div className="warehouses-view__modal-body">
              <div className="warehouses-view__modal-sidebar">
                <div style={{ padding: '1rem', borderBottom: '1px solid var(--color-brand-blue-lighter)', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.625rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: '#fff', textTransform: 'uppercase' }}>Histórico</span>
                  <button onClick={() => setSelectedAudit(null)} style={{ background: 'transparent', border: '1px solid var(--color-brand-green)', color: 'var(--color-brand-green)', fontSize: '0.5rem', fontFamily: 'var(--font-mono)', padding: '0.25rem 0.5rem', cursor: 'pointer' }}>NUEVO</button>
                </div>
                <div style={{ padding: '1rem' }}>
                  {inventoryLogs.filter(l => l.warehouseId === selectedWarehouse.id).map(log => (
                    <div key={log.id} onClick={() => setSelectedAudit(log)} className={`warehouses-view__history-item ${selectedAudit?.id === log.id ? 'selected' : ''}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#fff', fontWeight: 700, fontSize: '0.625rem' }}><span>{log.id}</span><span>{log.date}</span></div>
                      <div style={{ color: 'var(--color-brand-text)', fontSize: '0.5rem', marginTop: '0.25rem' }}>Desv: <span style={{ color: log.difference === 0 ? 'var(--color-brand-text)' : log.difference > 0 ? 'var(--color-brand-green)' : '#f87171' }}>{log.difference}</span></div>
                    </div>
                  ))}
                  {inventoryLogs.filter(l => l.warehouseId === selectedWarehouse.id).length === 0 && <div style={{ fontSize: '0.5625rem', fontFamily: 'var(--font-mono)', color: 'var(--color-brand-text)', textAlign: 'center' }}>Sin registros.</div>}
                </div>
              </div>
              <div className="warehouses-view__modal-main">
                {selectedAudit ? (
                  <div className="module-space-y-4">
                    <h4 style={{ fontSize: '0.875rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: '#fff' }}>AUDITORÍA {selectedAudit.id}</h4>
                    <div className="module-stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                      <div className="module-stat-card"><span className="module-stat-label">Esperado</span><span className="module-stat-value">{selectedAudit.expectedItems}</span></div>
                      <div className="module-stat-card"><span className="module-stat-label">Contado</span><span className="module-stat-value">{selectedAudit.countedItems}</span></div>
                      <div className="module-stat-card"><span className="module-stat-label">Desviación</span><span className="module-stat-value" style={{ color: selectedAudit.difference === 0 ? 'inherit' : selectedAudit.difference > 0 ? 'var(--color-brand-green)' : '#f87171' }}>{selectedAudit.difference}</span></div>
                    </div>
                    <div className="warehouses-view__table-container">
                      <table className="warehouses-view__table">
                        <thead><tr><th>Libro ID</th><th>Título</th><th style={{ textAlign: 'right' }}>Esperado</th><th style={{ textAlign: 'right' }}>Contado</th><th style={{ textAlign: 'right' }}>Desv.</th></tr></thead>
                        <tbody>
                          {selectedAudit.details.map(d => (
                            <tr key={d.bookId}>
                              <td>{d.bookId}</td><td>{d.bookTitle}</td><td style={{ textAlign: 'right' }}>{d.expectedStock}</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{d.countedStock}</td>
                              <td style={{ textAlign: 'right', color: d.difference === 0 ? 'var(--color-brand-text)' : d.difference > 0 ? 'var(--color-brand-green)' : '#f87171' }}>{d.difference}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="module-space-y-4">
                    <h4 style={{ fontSize: '0.875rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: '#fff' }}>NUEVO INVENTARIO FÍSICO</h4>
                    <div className="module-form-grid-2">
                      <div className="module-form-group"><label className="module-form-label">Auditor</label><input type="text" value={newAuditResponsible} onChange={e => setNewAuditResponsible(e.target.value)} className="module-form-input" /></div>
                      <div className="module-form-group"><label className="module-form-label">Fecha</label><input type="date" value={newAuditDate} onChange={e => setNewAuditDate(e.target.value)} className="module-form-input" /></div>
                    </div>
                    <div className="warehouses-view__table-container">
                      <table className="warehouses-view__table">
                        <thead><tr><th>ID</th><th>Título</th><th style={{ textAlign: 'right' }}>Esperado</th><th style={{ textAlign: 'right' }}>Real</th><th style={{ textAlign: 'right' }}>Desv.</th></tr></thead>
                        <tbody>
                          {stockLoading && <tr><td colSpan={5}>Cargando existencias del almacén...</td></tr>}
                          {!stockLoading && stockError && <tr><td colSpan={5}>{stockError}</td></tr>}
                          {!stockLoading && !stockError && warehouseStock.length === 0 && <tr><td colSpan={5}>Este almacén no tiene existencias registradas.</td></tr>}
                          {warehouseStock.map(r => ({ id: r.bookId, title: r.title, expected: r.stock })).map(b => {
                            const expected = b.expected;
                            const count = newCounts[b.id] ?? expected;
                            const diff = count - expected;
                            return (
                              <tr key={b.id}>
                                <td>{b.id}</td><td>{b.title}</td><td style={{ textAlign: 'right' }}>{expected}</td>
                                <td style={{ textAlign: 'right' }}>
                                  <div className="warehouses-view__qty-controls">
                                    <button onClick={() => setNewCounts(p => ({ ...p, [b.id]: Math.max(0, count - 1) }))} className="warehouses-view__qty-btn">-</button>
                                    <input type="number" min="0" value={count} onChange={e => setNewCounts(p => ({ ...p, [b.id]: parseInt(e.target.value) || 0 }))} className="warehouses-view__qty-input" />
                                    <button onClick={() => setNewCounts(p => ({ ...p, [b.id]: count + 1 }))} className="warehouses-view__qty-btn">+</button>
                                  </div>
                                </td>
                                <td style={{ textAlign: 'right', color: diff === 0 ? 'var(--color-brand-text)' : diff > 0 ? 'var(--color-brand-green)' : '#f87171' }}>{diff}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem' }}>
                      <button onClick={() => setIsInventoryOpen(false)} className="warehouses-view__modal-btn-primary">Guardar Auditoría</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal (Simple version of Inventory Modal) */}
      {isReceiptOpen && selectedWarehouse && (
        <div className="warehouses-view__modal-overlay" data-keep-detail>
          <div className="warehouses-view__modal-container">
            <div className="warehouses-view__modal-header">
              <div>
                <h3 className="warehouses-view__modal-title" style={{ color: '#38bdf8' }}><Truck size={16} /> Recepción de Mercancía</h3>
                <p className="warehouses-view__modal-subtitle">Almacén: <strong>{selectedWarehouse.name}</strong></p>
              </div>
              <button onClick={() => setIsReceiptOpen(false)} className="warehouses-view__modal-close"><X size={16} /></button>
            </div>
            <div className="warehouses-view__modal-body">
              <div className="warehouses-view__modal-main">
                <div className="module-space-y-4">
                  <div className="module-form-grid-3">
                    <div className="module-form-group"><label className="module-form-label">Albarán</label><input type="text" value={newReceiptNoteNum} onChange={e => setNewReceiptNoteNum(e.target.value)} className="module-form-input" /></div>
                    <div className="module-form-group"><label className="module-form-label">Origen</label><input type="text" value={newReceiptSource} onChange={e => setNewReceiptSource(e.target.value)} className="module-form-input" /></div>
                    <div className="module-form-group"><label className="module-form-label">Receptor</label><input type="text" value={newReceiptReceiver} onChange={e => setNewReceiptReceiver(e.target.value)} className="module-form-input" /></div>
                  </div>
                  <div className="warehouses-view__table-container">
                    <table className="warehouses-view__table">
                      <thead><tr><th>ID</th><th>Título</th><th style={{ textAlign: 'right' }}>Recibido</th></tr></thead>
                      <tbody>
                        {books.map(b => (
                          <tr key={b.id}>
                            <td>{b.id}</td><td>{b.title}</td>
                            <td style={{ textAlign: 'right' }}>
                              <div className="warehouses-view__qty-controls">
                                <button onClick={() => setNewReceiptQuantities(p => ({ ...p, [b.id]: Math.max(0, (p[b.id] || 0) - 10) }))} className="warehouses-view__qty-btn">-</button>
                                <input type="number" min="0" value={newReceiptQuantities[b.id] || 0} onChange={e => setNewReceiptQuantities(p => ({ ...p, [b.id]: parseInt(e.target.value) || 0 }))} className="warehouses-view__qty-input" />
                                <button onClick={() => setNewReceiptQuantities(p => ({ ...p, [b.id]: (p[b.id] || 0) + 10 }))} className="warehouses-view__qty-btn">+</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem' }}>
                    <span style={{ fontSize: '0.875rem', fontFamily: 'var(--font-mono)', color: '#fff', fontWeight: 700 }}>Total a recibir: {getIncomingTotal()}</span>
                    <button onClick={() => setIsReceiptOpen(false)} className="warehouses-view__modal-btn-primary" style={{ backgroundColor: '#38bdf8', borderColor: '#38bdf8' }}>Procesar Entrada</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={!!deleteModalWarehouse}
        type="urgent"
        title="Eliminar almacén"
        message={`¿Está seguro de que desea eliminar el almacén "${deleteModalWarehouse?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDeleteWarehouse}
        onCancel={() => setDeleteModalWarehouse(null)}
      />
    </>
  );
};
